import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  List,
} from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusDot } from '@/components/common/StatusBadge';
import { ProfileBadge } from '@/components/common/ProfileBadge';
import { ExportMenu } from '@/components/common/ExportMenu';
import { TablePagination } from '@/components/common/TablePagination';
import { NodeLifecycleBar } from '@/components/dashboard/NodeLifecycleBar';
import { DeploymentsChart } from '@/components/dashboard/DeploymentsChart';
import { EOLAlert } from '@/components/dashboard/EOLAlert';
import { CertExpiryAlert } from '@/components/dashboard/CertExpiryAlert';
import { SpareInventoryCard } from '@/components/dashboard/SpareInventoryCard';
import { ResourceAlertCard } from '@/components/dashboard/ResourceAlertCard';
import { useNodes } from '@/hooks/useNodes';
import { usePortfolioMetrics, useDeploymentStats } from '@/hooks/useMetrics';
import { formatTimeAgo } from '@/utils/formatters';
import {
  createBrandedPdf,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv } from '@/utils/csv';
import { PROFILE_TYPES, NODE_STATES, LIFECYCLE_STATES } from '@/utils/constants';
import { parseHardware } from '@/utils/calculations';
import { FilterChip } from '@/components/common/FilterChip';
import type { LifecycleState, ProductTier } from '@/types/node';

const TIER_OPTIONS: ProductTier[] = ['Basic', 'Pro', 'Enterprise'];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

type SortField =
  | 'currentNodeState'
  | 'name'
  | 'portfolio'
  | 'profileName'
  | 'softwareVersion'
  | 'hardwareSerialNumber'
  | 'deviceVendorDetails'
  | 'lastContactedAt'
  | 'firstContactedAt'
  | 'createdAt';

type SortOrder = 'asc' | 'desc';

export function AllNodes() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('currentNodeState');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [profileFilter, setProfileFilter] = useState<string>('');
  const [portfolioFilter, setPortfolioFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<ProductTier | ''>('');
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleState | null>(null);
  const [commissionedDays, setCommissionedDays] = useState(7);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { portfolios } = usePortfolioMetrics();
  const { stats: deploymentStats } = useDeploymentStats();

  const { nodes, total, loading } = useNodes({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    state: lifecycleFilter ? undefined : stateFilter || undefined,
    profile: profileFilter || undefined,
    portfolio: portfolioFilter
      ? portfolioFilter === '__blank__'
        ? ''
        : portfolioFilter
      : undefined,
    lifecycle: lifecycleFilter || undefined,
    tier: tierFilter || undefined,
    commissionedWithinDays:
      lifecycleFilter === 'recentlyCommissioned' ? commissionedDays : undefined,
    sortBy,
    sortOrder,
  });

  const totalPages = Math.ceil(total / pageSize);

  // Stabilize table wrapper height to prevent page jump during search
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const stableHeightRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (!tableWrapperRef.current) return;
    if (!loading && nodes.length >= pageSize) {
      stableHeightRef.current = tableWrapperRef.current.offsetHeight;
      tableWrapperRef.current.style.minHeight = '';
    } else if (stableHeightRef.current > 0) {
      tableWrapperRef.current.style.minHeight = `${stableHeightRef.current}px`;
    }
  }, [nodes, loading, pageSize]);

  // Fetch all nodes for export (not just current page)
  const { nodes: allNodes } = useNodes({ limit: 1000, commissionedWithinDays: commissionedDays });

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      // Default: offline first for status, ascending for everything else
      setSortOrder(field === 'currentNodeState' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field)
      return <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="dls-h-3.5 dls-w-3.5" />
    ) : (
      <ArrowDown className="dls-h-3.5 dls-w-3.5" />
    );
  };

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded ${className?.includes('text-center') ? 'mx-auto' : '-ml-1'}`}
      >
        {children}
        <SortIcon field={field} />
      </button>
    </TableHead>
  );

  const hasFilters =
    debouncedSearch || stateFilter || profileFilter || portfolioFilter || tierFilter || lifecycleFilter;

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStateFilter('');
    setProfileFilter('');
    setPortfolioFilter('');
    setTierFilter('');
    setLifecycleFilter(null);
    setPage(1);
  };

  const handleLifecycleFilterChange = (filter: LifecycleState | null) => {
    setLifecycleFilter(filter);
    // Lifecycle and state filters are mutually exclusive
    if (filter) setStateFilter('');
    setPage(1);
  };

  const handleStateFilterChange = (value: string) => {
    setStateFilter(value === 'all' ? '' : value);
    // Lifecycle and state filters are mutually exclusive
    if (value !== 'all') setLifecycleFilter(null);
    setPage(1);
  };

  const formatExportDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString() : '';

  const getLifecycleLabel = (n: (typeof allNodes)[number]): string => {
    if (n.lifecycleState) {
      return (
        LIFECYCLE_STATES[n.lifecycleState]?.label ??
        (n.currentNodeState === 'ALIVE' ? 'Online' : 'Offline')
      );
    }
    return n.currentNodeState === 'ALIVE' ? 'Online' : 'Offline';
  };

  const handleExportPdf = async () => {
    if (!allNodes.length) return;

    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'Nodes',
      subtitle: `${allNodes.length} Nodes`,
      logoData,
    });

    addBrandedTable(
      layout.doc,
      [
        'Status',
        'Name',
        'Portfolio',
        'Profile',
        'Tier',
        'S/N',
        'Vendor',
        'Model',
        'Version',
        'Services',
        'Last Contact',
        'First Contact',
        'Created',
      ],
      allNodes.map((n) => [
        getLifecycleLabel(n),
        n.name,
        n.portfolio || 'Unassigned',
        n.profileName || '',
        n.tier ?? '',
        n.hardwareSerialNumber || '',
        parseHardware(n).vendor,
        parseHardware(n).model || '',
        n.softwareVersion || '',
        n.serviceCount ?? 0,
        formatExportDate(n.lastContactedAt),
        formatExportDate(n.firstContactedAt),
        formatExportDate(n.createdAt),
      ]),
      layout.startY,
      layout
    );

    downloadPdf(layout.doc, exportFilename('all-nodes', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!allNodes.length) return;

    downloadCsv(
      allNodes.map((n) => ({
        Status: getLifecycleLabel(n),
        Name: n.name,
        Portfolio: n.portfolio || 'Unassigned',
        Profile: n.profileName || '',
        Tier: n.tier ?? '',
        'S/N': n.hardwareSerialNumber || '',
        Vendor: parseHardware(n).vendor,
        Model: parseHardware(n).model || '',
        Version: n.softwareVersion || '',
        Services: n.serviceCount ?? 0,
        'Last Contact': formatExportDate(n.lastContactedAt),
        'First Contact': formatExportDate(n.firstContactedAt),
        Created: formatExportDate(n.createdAt),
      })),
      exportFilename('all-nodes', 'csv'),
      { report: 'Nodes' }
    );
  };

  return (
    <div className="dls-space-y-4">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><List className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Nodes</h1>
          <p className="dls-text-muted-foreground">{total} Nodes total</p>
        </div>
        <ExportMenu
          onExportPdf={handleExportPdf}
          onExportCsv={handleExportCsv}
          label="Export Nodes"
        />
      </div>

      {/* Lifecycle Insights Bar */}
      <NodeLifecycleBar
        activeFilter={lifecycleFilter}
        onFilterChange={handleLifecycleFilterChange}
        commissionedDays={commissionedDays}
        onCommissionedDaysChange={setCommissionedDays}
      />

      {/* Deployment & Inventory Insights */}
      {deploymentStats && (
        <>
          {deploymentStats.deploymentsByQuarter.length > 0 && (
            <DeploymentsChart stats={deploymentStats} />
          )}
          <div className="dls-grid dls-grid-cols-1 md:dls-grid-cols-2 dls-gap-4">
              {deploymentStats.eolHardware.totalEol > 0 && (
                <EOLAlert eolHardware={deploymentStats.eolHardware} />
              )}
              {deploymentStats.certExpiry.expiringCount > 0 && (
                <CertExpiryAlert certExpiry={deploymentStats.certExpiry} />
              )}
              {(deploymentStats.spareInventory.totalSpares > 0 ||
                deploymentStats.spareInventory.eolSparesCount > 0) && (
                <SpareInventoryCard spareInventory={deploymentStats.spareInventory} />
              )}
              <ResourceAlertCard resourceAlerts={deploymentStats.resourceAlerts} />
            </div>
        </>
      )}

      {/* Table */}
      <div ref={tableWrapperRef}>
      <Card>
        <CardHeader>
          <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
            <CardTitle>Node Directory</CardTitle>
            <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
              {debouncedSearch && (
                <FilterChip
                  label={`Search: "${debouncedSearch}"`}
                  onRemove={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }}
                />
              )}
              {lifecycleFilter && (
                <FilterChip
                  label={`${LIFECYCLE_STATES[lifecycleFilter].label}${lifecycleFilter === 'recentlyCommissioned' ? ` (${commissionedDays}d)` : ''}`}
                  onRemove={() => { setLifecycleFilter(null); setPage(1); }}
                  className={
                    lifecycleFilter === 'new'
                      ? 'dls-bg-status-info/15 dls-text-status-info'
                      : lifecycleFilter === 'recentlyCommissioned'
                        ? 'dls-bg-neeve-tertiary/20 dls-text-neeve-primary'
                        : undefined
                  }
                />
              )}
              {stateFilter && (
                <FilterChip
                  label={`Status: ${stateFilter === NODE_STATES.ALIVE ? 'Online' : 'Offline'}`}
                  onRemove={() => { setStateFilter(''); setPage(1); }}
                />
              )}
              {profileFilter && (
                <FilterChip
                  label={`Profile: ${profileFilter}`}
                  onRemove={() => { setProfileFilter(''); setPage(1); }}
                />
              )}
              {portfolioFilter && (
                <FilterChip
                  label={`Portfolio: ${portfolioFilter === '__blank__' ? 'Unassigned' : portfolioFilter}`}
                  onRemove={() => { setPortfolioFilter(''); setPage(1); }}
                />
              )}
              {tierFilter && (
                <FilterChip
                  label={`Tier: ${tierFilter}`}
                  onRemove={() => { setTierFilter(''); setPage(1); }}
                />
              )}
              {hasFilters && (
                <Button variant="ghost" size="sm" className="dls-h-6 dls-text-xs" onClick={clearFilters}>
                  Clear all
                </Button>
              )}
              <div className="dls-relative">
                <Search className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                <Input
                  placeholder="Search any field..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="dls-pl-9 dls-w-full sm:dls-w-64 dls-rounded-xl"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className={`dls-p-0 ${!loading && nodes.length > 0 ? 'dls-overflow-x-auto' : ''}`}>
            <Table className={!loading && nodes.length > 0 ? 'dls-min-w-[1400px]' : ''}>
              <TableHeader>
                <TableRow>
                  {/* Status — sortable + filterable */}
                  <TableHead className="dls-w-[100px] dls-text-center">
                    <div className="dls-flex dls-items-center dls-justify-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('currentNodeState')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Status
                        <SortIcon field="currentNodeState" />
                      </button>
                      <Select
                        value={lifecycleFilter === 'new' ? 'new' : stateFilter || 'all'}
                        onValueChange={(v) => {
                          if (v === 'new') {
                            handleLifecycleFilterChange('new');
                          } else {
                            handleStateFilterChange(v);
                          }
                        }}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter status</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value={NODE_STATES.ALIVE}>Online</SelectItem>
                          <SelectItem value={NODE_STATES.UNREACHABLE}>Offline</SelectItem>
                          <SelectItem value="new">New (Never Contacted)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  <SortableHeader field="name">Name</SortableHeader>

                  {/* Portfolio — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('portfolio')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Portfolio
                        <SortIcon field="portfolio" />
                      </button>
                      <Select
                        value={portfolioFilter || 'all'}
                        onValueChange={(v) => {
                          setPortfolioFilter(v === 'all' ? '' : v);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter portfolio</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {portfolios.map((p) => (
                            <SelectItem
                              key={p.portfolio || '__blank__'}
                              value={p.portfolio || '__blank__'}
                            >
                              {p.portfolio || 'Unassigned'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  {/* Profile — sortable + filterable */}
                  <TableHead className="dls-text-center">
                    <div className="dls-flex dls-items-center dls-justify-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('profileName')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Profile
                        <SortIcon field="profileName" />
                      </button>
                      <Select
                        value={profileFilter || 'all'}
                        onValueChange={(v) => {
                          setProfileFilter(v === 'all' ? '' : v);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter profile</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {PROFILE_TYPES.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  {/* Tier — filterable (cross-table derived; not sortable at DB level). */}
                  <TableHead className="dls-text-center">
                    <div className="dls-flex dls-items-center dls-justify-center dls-gap-1">
                      <span className="dls-px-1 dls-py-0.5">Tier</span>
                      <Select
                        value={tierFilter || 'all'}
                        onValueChange={(v) => {
                          setTierFilter(v === 'all' ? '' : (v as ProductTier));
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter tier</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {TIER_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  <SortableHeader field="hardwareSerialNumber" className="dls-text-center">
                    S/N
                  </SortableHeader>
                  <SortableHeader field="deviceVendorDetails">Vendor</SortableHeader>
                  <TableHead>Model</TableHead>
                  <SortableHeader field="softwareVersion" className="dls-text-center">
                    Version
                  </SortableHeader>
                  <TableHead className="dls-text-center">Services</TableHead>
                  <SortableHeader field="lastContactedAt">Last Contact</SortableHeader>
                  <SortableHeader field="firstContactedAt">First Contact</SortableHeader>
                  <SortableHeader field="createdAt">Created</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} className="dls-text-center dls-py-12">
                      <div className="dls-inline-block dls-animate-spin dls-rounded-full dls-h-6 dls-w-6 dls-border-b-2 dls-border-primary" />
                    </TableCell>
                  </TableRow>
                ) : nodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="dls-text-center dls-py-12 dls-text-sm dls-text-muted-foreground">
                      {hasFilters
                        ? 'No nodes found matching the current filters.'
                        : 'No nodes have been discovered yet.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  nodes.map((node) => (
                    <TableRow key={node.id}>
                      <TableCell className="dls-text-center">
                        <StatusDot
                          state={node.currentNodeState}
                          lifecycleState={node.lifecycleState}
                        />
                      </TableCell>
                      <TableCell>
                        <Link to={`/nodes/${node.id}`} className="dls-font-medium hover:dls-underline">
                          {node.name}
                        </Link>
                      </TableCell>
                      <TableCell>{node.portfolio || '-'}</TableCell>
                      <TableCell className="dls-text-center">
                        <ProfileBadge profileName={node.profileName} />
                      </TableCell>
                      <TableCell className="dls-text-center dls-text-sm dls-text-muted-foreground">
                        {node.tier ?? '-'}
                      </TableCell>
                      <TableCell className="dls-font-mono dls-text-sm dls-text-muted-foreground dls-text-center">
                        {node.hardwareSerialNumber || '-'}
                      </TableCell>
                      <TableCell className="dls-text-sm">{parseHardware(node).vendor}</TableCell>
                      <TableCell className="dls-text-sm dls-text-muted-foreground">{parseHardware(node).model || '-'}</TableCell>
                      <TableCell className="dls-font-mono dls-text-sm dls-text-center">
                        {node.softwareVersion || '-'}
                      </TableCell>
                      <TableCell className="dls-text-center dls-tabular-nums">
                        {node.serviceCount ?? 0}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-whitespace-nowrap">
                        {formatTimeAgo(node.lastContactedAt)}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-whitespace-nowrap">
                        {formatTimeAgo(node.firstContactedAt ?? null)}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-whitespace-nowrap">
                        {formatTimeAgo(node.createdAt ?? null)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

          <TablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
